/**
 * What each paid plan actually sells, in one place, so every paywall explains the same
 * product. Checked 2026-09-24 against:
 * - startups3 `config/products/accelerator-{tools,advisor}.js` paywall lines
 * - startups3 `config/productPermissions/{tools,advisor}.js` (`investorMatchingMaxMessagesPerDay`
 *   3 vs 15, office hours and premium events on Advisor Team only)
 * - startups3 `ScoUser.remainingInvestorContactRequestsCount` (15 contact reveals per 24 hours, any paid plan)
 * - startups5 `Advisors/utils/upgradePaywall.ts` and `Advisors/model/access.ts` (Wil.ai, marketplace,
 *   board invites, FAST, the Startups.com roster)
 *
 * Slugs match the catalog so live `paywallOptions.features` lines merge instead of duplicating.
 * Add a line here only when code or the catalog backs it. Keep Wil.ai's product RAG in step:
 * startups3 `services/RagIngestionService/adapters/appFeatures.ts` (`plans`, `investor-matching`).
 *
 * `icon` is the bullet wherever a plan line is listed. Unknown catalog lines fall back to a check.
 * Product-card bullets are a separate, shorter composition in `cardLists.ts` (Rutan's benefit
 * library). This matrix stays the comparison-table and entitlement wording.
 */
import type { ScoProduct } from '@src/graphql-types'
import type { IconType } from 'react-icons'
import {
  LuBotMessageSquare,
  LuCalendarClock,
  LuContact,
  LuEye,
  LuFilePenLine,
  LuGift,
  LuGlobe,
  LuGraduationCap,
  LuHandshake,
  LuInbox,
  LuMail,
  LuMessageCircle,
  LuMessagesSquare,
  LuMic,
  LuPresentation,
  LuSearch,
  LuSend,
  LuSquarePlus,
  LuTarget,
  LuUserPlus,
  LuVideo,
  LuZap,
} from 'react-icons/lu'

export const PLAN_PRODUCT_IDS = {
  pro: 'scom-accelerator-tools',
  team: 'scom-accelerator-advisor',
} as const

/** Display id for the free account. Not a sold SKU and never sent to checkout. */
export const FREE_PLAN_ID = 'free'

/**
 * Same allotment as `Advisors/model/access` `FREE_WIL_MESSAGE_LIMIT`. Kept here so the plan
 * catalog does not import that module (it pulls checkout back into this file).
 * Card copy that names the count lives in `cardLists.ts` and calls this too.
 */
export function freeWilMessageAllowance(): number {
  return process.env.NEXT_PUBLIC_DEPLOYMENT_ENV === 'production' ? 50 : 5
}

const FREE_WIL_MESSAGES = freeWilMessageAllowance()

export type PlanTier = 'pro' | 'team' | 'free'

export type PlanFeatureLine = {
  title: string
  /** Short per-plan value for the comparison table when plans differ in amount, not presence. */
  value?: string
  /** When false, the line is a compare-table note only and stays off the plan card. */
  showOnCard?: boolean
}

export type PlanFeatureDefinition = {
  slug: string
  /** Bullet for this line in feature lists. */
  icon: IconType
  /** Row label in the comparison table when the per-plan titles carry numbers. */
  compareTitle?: string
  description?: string
  plans: Partial<Record<PlanTier, PlanFeatureLine>>
}

const both = (title: string): Partial<Record<PlanTier, PlanFeatureLine>> => ({
  pro: { title },
  team: { title },
})

const every = (title: string): Partial<Record<PlanTier, PlanFeatureLine>> => ({
  free: { title },
  pro: { title },
  team: { title },
})

/** Display order is the default order. Intents re-rank by slug. */
export const PLAN_FEATURES: PlanFeatureDefinition[] = [
  {
    slug: 'team-dm',
    icon: LuMessagesSquare,
    description: 'Message the Startups.com team from your Inbox.',
    plans: { team: { title: 'Direct messages with Ed, Ryan, Steve, and Wil' } },
  },
  {
    slug: 'wil-chat',
    icon: LuBotMessageSquare,
    compareTitle: 'Wil.ai chat',
    description: 'Free accounts have a message cap. Paid plans remove it.',
    plans: {
      free: {
        title: `${FREE_WIL_MESSAGES} Wil.ai messages`,
        value: `${FREE_WIL_MESSAGES} messages`,
      },
      pro: { title: 'Unlimited Wil.ai chat', value: 'Unlimited' },
      team: { title: 'Unlimited Wil.ai chat', value: 'Unlimited' },
    },
  },
  {
    slug: 'browse-advisors',
    icon: LuEye,
    description: 'Look through advisor profiles before you upgrade.',
    plans: every('Browse the advisor directory'),
  },
  {
    slug: 'board-seat',
    icon: LuSquarePlus,
    description: 'Post an open seat on your board.',
    plans: every('Open a board seat'),
  },
  {
    slug: 'board-applications',
    icon: LuInbox,
    description: 'Review people who apply to your seat.',
    plans: every('Accept applications on your seat'),
  },
  {
    slug: 'paid-sessions',
    icon: LuVideo,
    description: 'Book a session at the advisor’s listed rate. Sessions are not included in a plan.',
    plans: every('Book a paid session'),
  },
  {
    slug: 'marketplace-message',
    icon: LuMessageCircle,
    description: 'Start a conversation with any advisor in the directory.',
    plans: both('Message marketplace advisors'),
  },
  {
    slug: 'board-invite',
    icon: LuUserPlus,
    description: 'Recruit advisors onto an open seat on your board.',
    plans: both('Invite advisors to your board'),
  },
  {
    slug: 'fast-send',
    icon: LuFilePenLine,
    description: 'Offer advisor equity on the standard FAST agreement.',
    plans: both('Send & sign FAST agreements'),
  },
  {
    slug: 'expert-sessions',
    icon: LuCalendarClock,
    description: 'Join live office hours with the Startups.com team on Events.',
    plans: { team: { title: 'Team office hours' } },
  },
  {
    slug: 'advisor-sprints',
    icon: LuZap,
    plans: { team: { title: 'Premium events & advisor-led sprints' } },
  },
  {
    slug: 'messages-per-day',
    icon: LuSend,
    compareTitle: 'Investor messages per day',
    description: 'Outreach goes from your own connected email to your matched investors.',
    plans: {
      pro: { title: 'Send 3 Investor Messages per Day', value: '3 / day' },
      team: { title: 'Send 15 Investor Messages per Day', value: '15 / day' },
    },
  },
  {
    slug: 'investor-directory',
    icon: LuContact,
    description: 'Search investors and reveal up to 15 contacts a day.',
    plans: both('Investor Database'),
  },
  {
    slug: 'unlimited-matches',
    icon: LuSearch,
    compareTitle: 'Investor matches',
    description: 'Investors scored against your fundraise search.',
    plans: {
      free: { title: 'Preview of Investor Matches', value: 'Preview · first 10', showOnCard: false },
      pro: { title: 'Curated Investor Matches' },
      team: { title: 'Curated Investor Matches' },
    },
  },
  {
    slug: 'unlimited-outreach',
    icon: LuMail,
    compareTitle: 'Fundraise emails',
    description: 'AI-drafted emails for your investor matches.',
    plans: {
      free: { title: 'Preview of Fundraise Emails', value: 'Preview', showOnCard: false },
      pro: { title: 'Generated Fundraise Emails' },
      team: { title: 'Generated Fundraise Emails' },
    },
  },
  {
    slug: 'pitch-tools',
    icon: LuPresentation,
    compareTitle: 'Pitch deck audit',
    description: 'Draft investor emails and audit your pitch deck.',
    plans: {
      free: { title: 'Preview of Pitch Deck Audit', value: 'Preview · first 2 slides', showOnCard: false },
      pro: { title: 'AI-Powered Email Builder and Pitch Deck Auditor' },
      team: { title: 'AI-Powered Email Builder and Pitch Deck Auditor' },
    },
  },
  {
    slug: 'courses',
    icon: LuGraduationCap,
    compareTitle: 'Courses and videos',
    plans: {
      free: { title: 'Preview of video lessons', value: 'Preview · first 10 lessons', showOnCard: false },
      pro: { title: 'Guided Courses + Education' },
      team: { title: 'Guided Courses + Education' },
    },
  },
  {
    slug: 'goals',
    icon: LuTarget,
    plans: both('Create Goals for Funding, Advisors, and more'),
  },
  {
    slug: 'live-workshops',
    icon: LuMic,
    plans: both('Live Workshops & Peer Groups'),
  },
  {
    slug: 'member-matching',
    icon: LuHandshake,
    plans: both('Member Matching & Intros'),
  },
  {
    slug: 'global-founders',
    icon: LuGlobe,
    plans: both('Global Network of Founders'),
  },
  {
    slug: 'perks',
    icon: LuGift,
    plans: both('Over $25,000 in Partner Perks'),
  },
]

const KNOWN_SLUGS = new Set(PLAN_FEATURES.map(feature => feature.slug))

export function planTierFor(productId?: string | null): PlanTier | null {
  if (productId === FREE_PLAN_ID) {
    return 'free'
  }
  const entry = Object.entries(PLAN_PRODUCT_IDS).find(([, id]) => id === productId)
  return entry ? (entry[0] as PlanTier) : null
}

/** The free account as a plan tile. Price is $0; choosing it does not start checkout. */
export function freeMembershipProduct(): ScoProduct {
  return {
    id: FREE_PLAN_ID,
    productId: FREE_PLAN_ID,
    paywallName: 'Free',
    title: 'Free',
    termId: 'month',
    pricingInCents: { signup: 0, recurring: 0 },
    paywallOptions: {
      title: 'Free',
      description: 'Wil.ai with a message cap. Browse advisors, and upgrade when you want the rest.',
    },
  }
}

export function isPlanFeatureSlug(slug?: string | null): boolean {
  return Boolean(slug && KNOWN_SLUGS.has(slug))
}
